/**
 * Collision Detection Utilities
 * AABB (Axis-Aligned Bounding Box) collision detection for 2D game entities
 */

import type { Position } from '../types';

/**
 * Entity with collision properties
 */
export interface CollisionEntity {
  position: Position;
  size: number;
}

/**
 * Bounding box representation
 */
export interface BoundingBox {
  x: number;
  y: number;
  width: number;
  height: number;
}

/**
 * Get the bounding box for an entity with hitbox scaling
 * @param entity - Entity with position and size
 * @param hitboxScale - Scale factor for hitbox (0-1), default 1.0
 * @returns Bounding box centered on entity position
 */
export function getBoundingBox(
  entity: CollisionEntity,
  hitboxScale: number = 1.0
): BoundingBox {
  const scaledSize = entity.size * hitboxScale;
  return {
    x: entity.position.x - scaledSize / 2,
    y: entity.position.y - scaledSize / 2,
    width: scaledSize,
    height: scaledSize
  };
}

/**
 * Check if two bounding boxes overlap using AABB collision detection
 * @param box1 - First bounding box
 * @param box2 - Second bounding box
 * @returns True if boxes overlap
 */
export function boxesOverlap(box1: BoundingBox, box2: BoundingBox): boolean {
  return (
    box1.x < box2.x + box2.width &&
    box1.x + box1.width > box2.x &&
    box1.y < box2.y + box2.height &&
    box1.y + box1.height > box2.y
  );
}

/**
 * Calculate the overlap area between two bounding boxes
 * @param box1 - First bounding box
 * @param box2 - Second bounding box
 * @returns Overlap area in pixels squared, or 0 if no overlap
 */
export function getOverlapArea(box1: BoundingBox, box2: BoundingBox): number {
  if (!boxesOverlap(box1, box2)) {
    return 0;
  }

  const overlapX = Math.min(box1.x + box1.width, box2.x + box2.width) - Math.max(box1.x, box2.x);
  const overlapY = Math.min(box1.y + box1.height, box2.y + box2.height) - Math.max(box1.y, box2.y);

  return overlapX * overlapY;
}

/**
 * Get the area of a bounding box
 * @param box - Bounding box
 * @returns Area in pixels squared
 */
export function getBoxArea(box: BoundingBox): number {
  return box.width * box.height;
}

/**
 * Check if overlap meets the minimum threshold
 * Uses 40% of the smaller entity as threshold for child-friendly gameplay
 * @param box1 - First bounding box
 * @param box2 - Second bounding box
 * @param threshold - Minimum overlap percentage (0-1), default 0.4
 * @returns True if overlap meets threshold
 */
export function meetsOverlapThreshold(
  box1: BoundingBox,
  box2: BoundingBox,
  threshold: number = 0.4
): boolean {
  const overlapArea = getOverlapArea(box1, box2);
  if (overlapArea === 0) {
    return false;
  }

  const area1 = getBoxArea(box1);
  const area2 = getBoxArea(box2);
  const smallerArea = Math.min(area1, area2);

  const overlapPercentage = overlapArea / smallerArea;
  return overlapPercentage >= threshold;
}

/**
 * Check collision between two entities with configurable hitbox scales and threshold
 * @param entity1 - First entity
 * @param entity2 - Second entity
 * @param hitboxScale1 - Hitbox scale for entity1 (default 0.8 for player)
 * @param hitboxScale2 - Hitbox scale for entity2 (default 0.9 for objects)
 * @param threshold - Minimum overlap threshold (default 0.4)
 * @returns True if entities collide
 */
export function checkCollision(
  entity1: CollisionEntity,
  entity2: CollisionEntity,
  hitboxScale1: number = 0.8,
  hitboxScale2: number = 0.9,
  threshold: number = 0.4
): boolean {
  const box1 = getBoundingBox(entity1, hitboxScale1);
  const box2 = getBoundingBox(entity2, hitboxScale2);

  return meetsOverlapThreshold(box1, box2, threshold);
}

/**
 * Calculate the distance between two entities
 * @param entity1 - First entity
 * @param entity2 - Second entity
 * @returns Distance in pixels
 */
export function getDistance(entity1: CollisionEntity, entity2: CollisionEntity): number {
  const dx = entity2.position.x - entity1.position.x;
  const dy = entity2.position.y - entity1.position.y;
  return Math.sqrt(dx * dx + dy * dy);
}

/**
 * Check if two entities are within a certain distance (for broad-phase optimization)
 * @param entity1 - First entity
 * @param entity2 - Second entity
 * @param maxDistance - Maximum distance to consider
 * @returns True if entities are within max distance
 */
export function isWithinDistance(
  entity1: CollisionEntity,
  entity2: CollisionEntity,
  maxDistance: number
): boolean {
  const dx = entity2.position.x - entity1.position.x;
  const dy = entity2.position.y - entity1.position.y;
  const distanceSquared = dx * dx + dy * dy;
  const maxDistanceSquared = maxDistance * maxDistance;

  return distanceSquared <= maxDistanceSquared;
}

/**
 * Get the center point of a bounding box
 * @param box - Bounding box
 * @returns Center position
 */
export function getBoxCenter(box: BoundingBox): Position {
  return {
    x: box.x + box.width / 2,
    y: box.y + box.height / 2
  };
}
